/**
 * Document type mapping for accounting onboarding based on questionnaire answers
 */

import { type QuestionnaireAnswers } from '../components/UploadDocumentsQuestionnaire';

import { getCurrentTaxYear } from 'modules/Tax/constants';
import { getCurrentYear } from 'modules/Tax/utils/dates';

/**
 * Maps questionnaire answers to backend document types
 */
export const getDocumentTypesFromAnswers = (answers: QuestionnaireAnswers): string[] => {
    const documentTypes: string[] = [];

    // Always include bank statements
    documentTypes.push('BUSINESS_BANK_STATEMENT');

    // Add document types based on questionnaire answers
    if (answers.individualTaxReturn === 'yes') {
        documentTypes.push('INDIVIDUAL_TAX_RETURN_ORGANIZER');
    }

    if (answers.businessTaxReturn === 'yes') {
        documentTypes.push('BUSINESS_TAX_RETURN_ORGANIZER');
    }

    if (answers.businessVehicle === 'yes') {
        documentTypes.push('VEHICLE_PURCHASE_OR_LEASE_AGREEMENT');
    }

    if (answers.businessLoans === 'yes') {
        documentTypes.push('LOAN_AGREEMENT');
    }

    if (answers.accountingSoftware === 'yes') {
        // Use GENERAL_LEDGER as a temporary solution for accounting software exports
        // TODO: Create ACCOUNTING_SOFTWARE_EXPORT document type in backend
        documentTypes.push('GENERAL_LEDGER');
    }

    return documentTypes;
};

/**
 * Gets the display name for a document type
 *
 * Year-bearing labels are derived, never written out: the questionnaire asks about
 * `getCurrentTaxYear()` and the upload card has to name that same year back, so both sides read
 * the one helper rather than each carrying a literal that goes stale at the rollover (AP-2734).
 */
export const getDocumentTypeDisplayName = (documentType: string): string => {
    const displayNames: Record<string, string> = {
        BUSINESS_BANK_STATEMENT: 'Bank Statement',
        INDIVIDUAL_TAX_RETURN_ORGANIZER: `${getCurrentTaxYear()} Individual Tax Return`,
        BUSINESS_TAX_RETURN_ORGANIZER: `${getCurrentTaxYear()} Business Tax Return`,
        VEHICLE_PURCHASE_OR_LEASE_AGREEMENT: 'Vehicle purchase agreement and related docs',
        LOAN_AGREEMENT: 'Loan documents showing the interest rate and terms',
        // The books being set up are the year in progress, not the year being filed
        GENERAL_LEDGER: `${getCurrentYear()} Profit & Loss Statement, Balance Sheets, and General Ledger`,
        PAYROLL_DOCUMENTATION: 'Paystubs for the current year',
    };

    return displayNames[documentType] || documentType;
};

/**
 * Gets the description for a document type
 */
export const getDocumentTypeDescription = (documentType: string): string => {
    const descriptions: Record<string, string> = {
        BUSINESS_BANK_STATEMENT: 'Most recent bank statement for your business account',
        INDIVIDUAL_TAX_RETURN_ORGANIZER: 'Your most recent individual tax return',
        BUSINESS_TAX_RETURN_ORGANIZER: 'Your most recent business tax return',
        VEHICLE_PURCHASE_OR_LEASE_AGREEMENT: 'Vehicle registration, loan documents, or lease agreements',
        LOAN_AGREEMENT: 'Loan agreements, promissory notes, or credit line documents',
        GENERAL_LEDGER: 'Export of your accounting software data',
        PAYROLL_DOCUMENTATION: 'Payroll reports, employee records, or payroll service exports',
    };

    return descriptions[documentType] || 'Document required for accounting setup';
};
